import type { RescuedLegacyShrinkOperation, ShrinkMobileState } from "~/store/shrinkMobileSlice";

import {
	type RecordShrinkEventDataInput,
	type RecordShrinkEventInput,
	type RemoveShrinkEventDataInput
} from "~/gql/graphqlBase.generated";
import logger from "~/helpers/logger";

export type LegacyShrinkOperationStatus = "failed" | "pending" | "posted" | "posting";

export interface LegacyShrinkQueuedOperation {
	id: string;
	input?: RecordShrinkEventDataInput | RemoveShrinkEventDataInput;
	queuedAt: string;
	status: LegacyShrinkOperationStatus;
	type: "record" | "remove";
}

export interface LegacyShrinkMobileState extends ShrinkMobileState {
	operationQueue?: LegacyShrinkQueuedOperation[];
}

type InputClassification<TInput> = { input: TInput; rescuable: true } | { missingField: string; rescuable: false };

const isUnsyncedLegacyShrinkOperation = (operation: LegacyShrinkQueuedOperation): boolean =>
	operation.status !== "posted";

const readNonEmptyString = (value: unknown): string | undefined =>
	typeof value === "string" && value.length > 0 ? value : undefined;

const classifyRecordInput = (
	input: LegacyShrinkQueuedOperation["input"]
): InputClassification<RecordShrinkEventDataInput> => {
	if (!input) return { missingField: "input", rescuable: false };
	const recordInput = input as Partial<RecordShrinkEventDataInput>;
	if (!readNonEmptyString(recordInput.productionJobId))
		return { missingField: "input.productionJobId", rescuable: false };
	if (!readNonEmptyString(recordInput.productionLotId))
		return { missingField: "input.productionLotId", rescuable: false };
	const innerInput = recordInput.input as Partial<RecordShrinkEventInput> | undefined;
	if (!innerInput) return { missingField: "input.input", rescuable: false };
	if (!readNonEmptyString(innerInput.shrinkEventId))
		return { missingField: "input.input.shrinkEventId", rescuable: false };
	return { input: recordInput as RecordShrinkEventDataInput, rescuable: true };
};

const classifyRemoveInput = (
	input: LegacyShrinkQueuedOperation["input"]
): InputClassification<RemoveShrinkEventDataInput> => {
	if (!input) return { missingField: "input", rescuable: false };
	const removeInput = input as Partial<RemoveShrinkEventDataInput>;
	if (!readNonEmptyString(removeInput.productionJobId))
		return { missingField: "input.productionJobId", rescuable: false };
	if (!readNonEmptyString(removeInput.productionLotId))
		return { missingField: "input.productionLotId", rescuable: false };
	if (!readNonEmptyString(removeInput.shrinkEventId)) return { missingField: "input.shrinkEventId", rescuable: false };
	return { input: removeInput as RemoveShrinkEventDataInput, rescuable: true };
};

const reportUnrescuableLegacyShrinkOperation = (operation: LegacyShrinkQueuedOperation, missingField: string): void => {
	logger.error(`[shrink] legacy operationQueue entry dropped on upgrade — missing ${missingField}, cannot replay`, {
		droppedOperation: { id: operation.id, missingField, queuedAt: operation.queuedAt, type: operation.type }
	});
};

const classifyLegacyShrinkOperation = (
	operation: LegacyShrinkQueuedOperation
): RescuedLegacyShrinkOperation | undefined => {
	if (operation.type === "record") {
		const classification = classifyRecordInput(operation.input);
		if (!classification.rescuable) {
			reportUnrescuableLegacyShrinkOperation(operation, classification.missingField);
			return undefined;
		}
		return { id: operation.id, input: classification.input, queuedAt: operation.queuedAt, type: "record" };
	}
	const classification = classifyRemoveInput(operation.input);
	if (!classification.rescuable) {
		reportUnrescuableLegacyShrinkOperation(operation, classification.missingField);
		return undefined;
	}
	return { id: operation.id, input: classification.input, queuedAt: operation.queuedAt, type: "remove" };
};

const stageRescuableLegacyShrinkOperations = (
	operationQueue: LegacyShrinkQueuedOperation[]
): RescuedLegacyShrinkOperation[] =>
	operationQueue.filter(isUnsyncedLegacyShrinkOperation).reduce<RescuedLegacyShrinkOperation[]>((staged, operation) => {
		const rescued = classifyLegacyShrinkOperation(operation);
		return rescued ? [...staged, rescued] : staged;
	}, []);

const migrateLegacyShrinkOperationQueue = (outboundState: LegacyShrinkMobileState): ShrinkMobileState => {
	const { operationQueue, ...rehydratedState } = outboundState;
	const alreadyPendingRescue = rehydratedState.pendingRescue ?? [];
	if (operationQueue === undefined) return { ...rehydratedState, pendingRescue: alreadyPendingRescue };
	return {
		...rehydratedState,
		pendingRescue: [...alreadyPendingRescue, ...stageRescuableLegacyShrinkOperations(operationQueue)]
	};
};

export default migrateLegacyShrinkOperationQueue;
